/** ========================================================================= *\
 * Form v0.1.0
 * A simple JavaScript form validation library.
 * 
 * @author James Ooi
 * @organization Forefront International
 * ========================================================================== */
import MoveTo from 'moveto';

class Form {

  // Static Properties ------------------------------------------------------ //

  static defaultOptions = {
    submitFn: null,
    onSubmit: null,
    onSuccess: null,
    onError: null,
    scrollToError: true,
  };

  // Class Properties ------------------------------------------------------- //

  options = Form.defaultOptions;
  dirty = false;

  // Constructor ------------------------------------------------------------ //

  constructor(element, options = {}) {
    // Set options
    this.options = { ...this.options, ...options };

    // Validate options
    if (typeof this.options.submitFn !== 'function') {
      console.error('Please provide a form submit handler.');
    }

    // Get element
    this.element = element;
    this.button = element.querySelector('[js-hook=submit-registration]');

    // Add default attribute
    this.element.setAttribute('data-status', 'default');

    // Collect inputs
    this.inputs = [
      ...Array.from(element.querySelectorAll('input') || []),
      ...Array.from(element.querySelectorAll('select') || []),
      ...Array.from(element.querySelectorAll('textarea') || []),
    ];

    // Attach listeners
    this._attachEventListeners();
  }

  // Public Methods --------------------------------------------------------- //

  /** Submit the form */
  submit() {
    const startTime = new Date();

    // Validate all inputs
    const errors = [];
    this.inputs.forEach((input) => {
      const inputErrors = this.validateInput(input);
      if (inputErrors.errors.length > 0) { errors.push(inputErrors); }
    });

    // If there are errors, scroll to first input with errors and return
    if (errors.length > 0) {
      if (this.options.scrollToError) {
        const moveTo = new MoveTo({ tolerance: 50 });
        moveTo.move(errors[0].input);
      }
      return;
    }

    // Submit the form
    this._onSubmit();
    const handler = this.options.submitFn(this.inputs, this.element);

    // Handle responses
    handler
      .then((...args) => this._onSuccess(...args))
      .catch((errorMessage) => this._onError(errorMessage));

    return handler;
  }

  /**
   * Validate a single HTML input
   * @param {HTMLInputElement} input
   */
  validateInput(input) {
    const errors = [];

    // Check required
    if (input.hasAttribute('required')) {
      if (input.getAttribute('type') === 'checkbox') {
        if (!input.checked) {
          errors.push('This field is required.');
        }
      } else {
        if (input.value === '') {
          errors.push('This field is required.');
        }
      }
    }

    // Check max length
    if (input.hasAttribute('max-length')) {
      const maxLength = Number(input.getAttribute('max-length'));
      if (input.value.length > maxLength) {
        errors.push(`This field cannot be more than ${maxLength} characters.`);
      }
    }

    // Check min length
    if (input.hasAttribute('min-length')) {
      const minLength = Number(input.getAttribute('min-length'));
      if (input.value.length < minLength) {
        errors.push(`This field cannot be less than ${minLength} characters.`);
      }
    }
    
    // Check pattern
    if (input.hasAttribute('pattern')) {
      const pattern = new RegExp(input.getAttribute('pattern'));
      if (!pattern.test(input.value)) {
        errors.push('This field is invalid.');
      }
    }

    // Check email address
    if (input.getAttribute('type') === 'email') {
      const pattern = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      if (!pattern.test(input.value)) {
        errors.push('Please provide a valid email address.');
      }
    }

    const errObject = {
      input: input,
      errors: errors
    }

    // Display errors
    this._displayError(errObject);

    return errObject;
  }

  /**
   * Sets the state of this form. Can be either 'loading', 'error', or 'success'
   * @param {String} state 
   */
  setState(state, ...args) {
    switch (state.toLowerCase()) {
      case 'loading':
        this._onSubmit();
        break;
      case 'success':
        this._onSuccess(...args);
        break;
      case 'error':
        this._onError(...args);
        break;
    }
    return this;
  }

  /** @private Methods ------------------------------------------------------ */

  /**
   * Attach default event listeners to form
   */
  _attachEventListeners() {
    // Activate submit button
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.submit();
    });

    // Validate fields on input
    this.inputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.validateInput(input);
        // Set dirty
        if (!this.dirty) { this.dirty = true; }
      });
    });
  }

  /**
   * Display an error message into its error container
   * @param {ErrObject} errObject
   * @param {Boolean} showAll Show all error messages. Defaults to show only the
   *  first error message.
   */
  _displayError(errObject, showAll = false) {
    const inputName = errObject.input.name;

    // Get error container
    const errorContainer = this.element.querySelector(`[data-error-element][for=${inputName}]`);
    if (!errorContainer) return;

    // Hide error container if there are no errors and exit
    if (errObject.errors.length < 1) {
      errorContainer.style.display = 'none';
      return;
    }

    // Create error message
    const errorMessage = showAll
      ? errObject.errors.join(' ')
      : errObject.errors[0];

    // Display error message
    errorContainer.style.display = 'block';
    errorContainer.innerHTML = errorMessage;
  }

  /**
   * Reset the form elements UI
   * @param {Boolean} clearValues - Clear the input values. Defaults to false.
   */
  reset(clearValues = false) {
    this.element.setAttribute('data-status', 'default');

    // Remove classes
    this.button.classList.remove('is-loading');
    this.button.classList.remove('is-success');

    // Enable inputs and buttons
    this.button.disabled = false;
    this.inputs.forEach(input => input.disabled = false);

    // Clear values
    if (clearValues) {
      this.inputs.forEach(input => input.value = '');
    }
  }

  /** @callback Methods ----------------------------------------------------- */

  /** onSubmit */
  _onSubmit() {
    // Update form attribute
    this.element.setAttribute('data-status', 'loading');

    // Disable button
    this.button.disabled = true;

    // Disable form inputs
    this.inputs.forEach(input => input.disabled = true);

    // Call user provided callback function
    if (typeof this.options.onSubmit === 'function') {
      this.options.onSubmit(this);
    }
  }

  /** onSuccess */
  _onSuccess(...args) {
    // Update form attribute
    this.element.setAttribute('data-status', 'success');

    // Enable button
    this.button.disabled = false;

    // Enable form inputs
    this.inputs.forEach(input => input.disabled = false);

    // Clear form inputs
    this.inputs.forEach(input => {
      input.value = '';
      input.checked = false;
    });

    // Call user provided callback function
    if (typeof this.options.onSuccess === 'function') {
      this.options.onSuccess(...args);
    }
  }

  /** onError */
  _onError(errorMessage) {
    // Update form attribute
    this.element.setAttribute('data-status', 'error');

    // Enable button
    this.button.disabled = false;

    // Enable form inputs
    this.inputs.forEach(input => input.disabled = false);

    // Show alert
    window.alert(`We're unable to process your request right now. Please try again later.\n${errorMessage}`);

    // Call user provided callback function
    if (typeof this.options.onError === 'function') {
      this.options.onError(errorMessage);
    }
  }

}

export default Form;
